import { iamLog } from '@identity/application/observability';
import type { RequestPasswordRecoveryDto } from '@identity/application/dtos';
import type { IAuthApiPort } from '@identity/application/ports';
import { extractErrorAttributes, retryAfterAttributes } from '@identity/application/utils';
import type { IdentityFlow } from '@identity/domain/types';
import { createEmail } from '@identity/domain/value-objects';

/**
 * Asks the backend to deliver a recovery link, and never learns whether it did.
 *
 * The answer is the same whether the address belongs to a verified account, to a deleted one or to
 * nobody at all, so nothing here may branch on it. The format is checked first with the value
 * object, which is the only thing that can be known about the address without asking about it.
 *
 * Its event carries no subject and no address. There is no account id, because this call is
 * authorized by nothing and the answer names nobody; and an address here would build a searchable
 * record of who tried to recover an account, including addresses that belong to no one, which is
 * exactly the enumeration the identical answer exists to prevent.
 */
export const requestPasswordRecoveryUseCase = (authApi: IAuthApiPort, flow: IdentityFlow) => {
  const execute = async (dto: RequestPasswordRecoveryDto): Promise<void> => {
    const { email, isResend = false } = dto;
    const address = createEmail(email);
    const tags = { 'iam.flow': flow, 'iam.isResend': isResend };

    try {
      await authApi.requestPasswordRecovery(address.value);

      iamLog.info('passwordRecoveryRequested', tags);
    } catch (error) {
      iamLog.error('passwordRecoveryRequested', {
        ...tags,
        ...extractErrorAttributes(error),
        ...retryAfterAttributes(error),
      });
      throw error;
    }
  };

  return { execute };
};
